import { useEffect, useState } from "react";
import { Sparkle, ShieldCheck, DownloadSimple } from "@phosphor-icons/react";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";

export default function ConsoleAiTransparency() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/console/ai-transparency");
        if (alive) setData(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const onDownload = async () => {
    setDownloading(true);
    try {
      await downloadBlob("/console/ai-transparency/pdf", "ClaroShield_AI_Transparency.pdf");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground py-8" data-testid="ai-transparency-loading">Loading…</div>;
  if (!data) return <div className="text-sm text-muted-foreground py-8">Unable to load AI transparency content.</div>;

  return (
    <div className="max-w-3xl" data-testid="ai-transparency">
      <div className="flex items-start justify-between gap-4 border-b border-border pb-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-primary" />
            <h2 className="text-lg font-semibold tracking-tight" data-testid="ai-transparency-title">{data.title}</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{data.subtitle} · Updated {data.updated}</p>
        </div>
        <Button onClick={onDownload} disabled={downloading} className="rounded-none shrink-0" data-testid="ai-transparency-download">
          <DownloadSimple size={16} weight="bold" className="mr-2" />
          {downloading ? "Preparing…" : "Download PDF"}
        </Button>
      </div>

      <p className="text-sm leading-relaxed text-foreground/90 mb-8">{data.intro}</p>

      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Models in use</h3>
      <div className="grid gap-3 mb-8">
        {data.models.map((m, i) => (
          <div key={i} className="border border-border p-4" data-testid={`ai-transparency-model-${i}`}>
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <span className="font-semibold text-sm">{m.name}</span>
              <span className="text-xs uppercase tracking-wide text-primary">{m.role}</span>
            </div>
            <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{m.detail}</p>
          </div>
        ))}
      </div>

      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Data handling &amp; safeguards</h3>
      <div className="space-y-4">
        {data.sections.map((s, i) => (
          <div key={i} className="flex gap-3" data-testid={`ai-transparency-section-${i}`}>
            <ShieldCheck size={18} weight="bold" className="text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-sm">{s.title}</div>
              <p className="text-sm text-foreground/80 leading-relaxed mt-0.5">{s.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
